import { isDevelopmentNodeEnvironment } from "@src/appV2/environment";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { useCallback, useEffect, useRef, useState } from "react";

import { type LogEntry, type LogLevel } from "../types";

interface UseConsoleInterceptorProps {
  maxLogs: number;
}

export function useConsoleInterceptor({ maxLogs }: UseConsoleInterceptorProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const originalConsole = useRef<{
    log: typeof console.log;
    info: typeof console.info;
    warn: typeof console.warn;
    error: typeof console.error;
    debug: typeof console.debug;
  }>();

  // Store original console methods
  useEffect(() => {
    originalConsole.current = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
      debug: console.debug,
    };
  }, []);

  const addLog = useCallback(
    (level: LogLevel, logArguments: unknown[]) => {
      const logEntry: LogEntry = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        level,
        args: logArguments,
        stack: level === "error" ? new Error("Stack trace").stack : undefined,
      };

      setLogs((previousLogs) => {
        const newLogs = [...previousLogs, logEntry];
        return newLogs.slice(-maxLogs); // Keep the last maxLogs entries
      });
    },
    [maxLogs]
  );

  // Override console methods
  useEffect(() => {
    if (!isDevelopmentNodeEnvironment() || !isCapacitorPlatform()) {
      return undefined;
    }

    // Store the current console methods before overriding
    const originalMethods = {
      log: originalConsole.current?.log ?? console.log,
      info: originalConsole.current?.info ?? console.info,
      warn: originalConsole.current?.warn ?? console.warn,
      error: originalConsole.current?.error ?? console.error,
      debug: originalConsole.current?.debug ?? console.debug,
    };

    const interceptConsole = (
      level: LogLevel,
      originalMethod: (...consoleArguments: unknown[]) => void
    ) => {
      return (...consoleArguments: unknown[]) => {
        try {
          // Call the original method directly (not through console.*)
          originalMethod.apply(console, consoleArguments);
        } catch {
          // If original method fails, don't crash the interceptor
          // This prevents infinite loops if the original method throws
        }

        try {
          // Add to mobile console
          addLog(level, consoleArguments);
        } catch {
          // If addLog fails, don't crash the console
          // This prevents console.log from breaking the app
        }
      };
    };

    console.log = interceptConsole("log", originalMethods.log);
    console.info = interceptConsole("info", originalMethods.info);
    console.warn = interceptConsole("warn", originalMethods.warn);
    console.error = interceptConsole("error", originalMethods.error);
    console.debug = interceptConsole("debug", originalMethods.debug);

    // Cleanup on unmount - ensure we always restore original methods
    return () => {
      try {
        console.log = originalMethods.log;
        console.info = originalMethods.info;
        console.warn = originalMethods.warn;
        console.error = originalMethods.error;
        console.debug = originalMethods.debug;
      } catch {
        // Silently handle cleanup errors to prevent crash on unmount
      }
    };
  }, [addLog]);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  return {
    logs,
    clearLogs,
  };
}
